import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Eye, Trash } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

export default function PackingLists() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [pendingBoxes, setPendingBoxes] = useState([]);
  const [settings, setSettings] = useState({ units_per_box: 170 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const [p, b, s] = await Promise.all([api.get("/packing-lists"), api.get("/boxes"), api.get("/settings")]);
    setItems(p.data);
    setPendingBoxes(b.data.filter((x) => !x.packing_list_id));
    setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this packing list? Boxes will be released.")) return;
    try { await api.delete(`/packing-lists/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="pls-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Packing Lists</h1>
          <p className="text-xs text-zinc-600 mt-1">Shipment groups · multiples of {settings.units_per_box} units</p>
        </div>
        {canEdit && (
          <button onClick={() => setOpen(true)} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-pl-btn">
            <Plus size={14} weight="bold" /> New Packing List
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="pls-table">
          <thead><tr><th>PL #</th><th>Customer</th><th>Ship Date</th><th>Boxes</th><th>Total Units</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No packing lists yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`pl-row-${p.pl_number}`}>
                <td className="font-bold">{p.pl_number}</td>
                <td>{p.customer}</td>
                <td className="font-mono-ui">{p.ship_date}</td>
                <td className="font-mono-ui">{p.total_boxes}</td>
                <td className="font-mono-ui font-bold">{p.total_units}</td>
                <td className="font-mono-ui">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="text-right">
                  <Link to={`/packing-lists/${p.id}`} className="brut-tag brut-tag-yellow mr-1" data-testid={`view-pl-${p.pl_number}`}>
                    <Eye size={10} weight="bold" />
                  </Link>
                  {canDelete && (
                    <button onClick={() => remove(p.id)} className="brut-tag brut-tag-red" data-testid={`delete-pl-${p.pl_number}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <NewPL
          boxes={pendingBoxes}
          onClose={() => setOpen(false)}
          onSaved={(pl) => { setOpen(false); navigate(`/packing-lists/${pl.id}`); }}
        />
      )}
    </div>
  );
}

function NewPL({ boxes, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer: "", ship_date: today, remarks: "" });
  const [selected, setSelected] = useState([]);

  const totalUnits = selected.reduce((acc, id) => acc + (boxes.find((b) => b.id === id)?.units_count || 0), 0);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer) return toast.error("Customer required");
    if (selected.length === 0) return toast.error("Select at least one box");
    try {
      const { data } = await api.post("/packing-lists", { ...form, box_ids: selected });
      toast.success(`Packing list ${data.pl_number} created`);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Modal title="New Packing List" onClose={onClose}>
      {boxes.length === 0 ? (
        <div className="brut-tag brut-tag-red w-full text-center" data-testid="no-boxes-warning">
          No pending boxes available. Assemble boxes first.
        </div>
      ) : (
        <form onSubmit={save} className="space-y-4" data-testid="pl-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer" required value={form.customer} onChange={(v) => setForm({ ...form, customer: v })} testid="pl-customer" />
            <Field label="Ship Date" type="date" required value={form.ship_date} onChange={(v) => setForm({ ...form, ship_date: v })} testid="pl-ship-date" />
          </div>
          <Field label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} textarea testid="pl-remarks" />

          <div>
            <div className="brut-label mb-2">Select Boxes ({selected.length} selected, {totalUnits} units)</div>
            <div className="brut-border max-h-72 overflow-y-auto">
              {boxes.map((b) => (
                <label key={b.id} className="flex items-center gap-3 px-3 py-2 border-b border-zinc-300 cursor-pointer hover:bg-[#eff6ff]" data-testid={`pl-box-option-${b.box_number}`}>
                  <input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggle(b.id)} className="w-4 h-4 brut-border" />
                  <div className="flex-1">
                    <div className="font-mono-ui text-sm font-bold">{b.box_number}</div>
                    <div className="text-xs text-zinc-600">{b.product_name} · {b.units_count} units · {new Date(b.created_at).toLocaleDateString()}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="brut-tag brut-tag-white mt-2" data-testid="pl-units-check">
              Total units selected: {totalUnits}
            </div>
          </div>

          <button type="submit" disabled={selected.length === 0} className="brut-btn brut-btn-primary px-4 py-2 disabled:opacity-50" data-testid="save-pl-btn">Create Packing List</button>
        </form>
      )}
    </Modal>
  );
}
